import { useEffect, useState } from 'react';

import { getApiBase } from '../apiBase';

const API_BASE = getApiBase();

const parseLocalDateTime = (value) => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/);
  if (!match) return null;
  const [, year, month, day, hour, minute, second] = match.map(Number);
  const date = new Date(year, month - 1, day, hour, minute, second);
  if (
    date.getFullYear() !== year
    || date.getMonth() !== month - 1
    || date.getDate() !== day
    || date.getHours() !== hour
    || date.getMinutes() !== minute
    || date.getSeconds() !== second
  ) return null;
  return date;
};

const formatDate = (date) => new Intl.DateTimeFormat('en-US', {
  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
}).format(date);

const formatTime = (date) => new Intl.DateTimeFormat('en-US', {
  hour: 'numeric', minute: '2-digit', hour12: true,
}).format(date);

const PublicAppointmentSchedulePage = ({ token }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const response = await fetch(`${API_BASE}/public/appointment-schedule/${encodeURIComponent(token || '')}`);
        if (!response.ok) throw new Error('unavailable');
        const data = await response.json();
        const schedule = Array.isArray(data.schedule) ? data.schedule.map((item) => ({
          start: parseLocalDateTime(item.start_time),
          end: parseLocalDateTime(item.end_time),
        })) : [];
        if (!schedule.length || schedule.some((item) => !item.start || !item.end || item.end <= item.start)) {
          throw new Error('unavailable');
        }
        if (active) setResult({ ...data, schedule });
      } catch (_error) {
        if (active) setUnavailable(true);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [token]);

  const pageStyle = {
    minHeight: '100vh', background: '#e9eef5', padding: 'clamp(12px, 4vw, 40px)',
    boxSizing: 'border-box', fontFamily: 'Arial, sans-serif', color: '#000000',
  };
  const cardStyle = {
    width: '100%', maxWidth: 780, margin: '0 auto', padding: 'clamp(16px, 4vw, 32px)',
    boxSizing: 'border-box', border: '4px solid #000000', background: '#ffffff',
  };

  if (loading || unavailable || !result) {
    return (
      <main style={pageStyle}>
        <div style={{ ...cardStyle, textAlign: 'center', fontWeight: 900 }}>
          {loading ? 'LOADING APPOINTMENT SCHEDULE…' : 'THIS APPOINTMENT SCHEDULE IS UNAVAILABLE.'}
        </div>
      </main>
    );
  }

  const businessName = String(result.business?.businessName || '').trim();
  const logoUrl = String(result.business?.logoUrl || '').trim();
  const customerName = String(result.customer?.name || '').trim();

  return (
    <main style={pageStyle}>
      <style>{`
        @media print {
          body { background: #fff !important; }
          .appointment-schedule-page { padding: 0 !important; background: #fff !important; }
          .appointment-schedule-card { max-width: none !important; border: 0 !important; }
          .appointment-schedule-print { display: none !important; }
        }
      `}</style>
      <div className="appointment-schedule-page">
        <section className="appointment-schedule-card" style={cardStyle}>
          <header style={{ textAlign: 'center', marginBottom: 24 }}>
            {logoUrl ? <img src={logoUrl} alt="" style={{ maxWidth: 220, maxHeight: 110, objectFit: 'contain' }} /> : null}
            {businessName ? <div style={{ marginTop: 10, fontSize: 'clamp(20px, 5vw, 30px)', fontWeight: 900 }}>{businessName}</div> : null}
            <h1 style={{ margin: '20px 0 0', fontSize: 'clamp(22px, 6vw, 34px)' }}>YOUR APPOINTMENT SCHEDULE</h1>
            {customerName ? <div style={{ marginTop: 10, fontSize: 18, fontWeight: 800 }}>{customerName}</div> : null}
          </header>

          <div style={{ display: 'grid', gap: 12 }}>
            {result.schedule.map((appointment, index) => {
              const crossesDate = appointment.start.toDateString() !== appointment.end.toDateString();
              return (
                <article key={`${appointment.start.getTime()}-${index}`} style={{ padding: 16, border: '3px solid #000000', borderRadius: 12 }}>
                  <div style={{ fontWeight: 900, fontSize: 'clamp(17px, 4vw, 21px)' }}>{formatDate(appointment.start)}</div>
                  <div style={{ marginTop: 6, fontWeight: 800 }}>
                    {formatTime(appointment.start)} – {crossesDate ? `${formatDate(appointment.end)}, ` : ''}{formatTime(appointment.end)}
                  </div>
                </article>
              );
            })}
          </div>

          <button
            type="button"
            className="appointment-schedule-print"
            onClick={() => window.print()}
            style={{
              width: '100%', minHeight: 52, marginTop: 24, padding: '12px 16px',
              boxSizing: 'border-box', border: '3px solid #000000', borderRadius: 12,
              background: '#FFC107', color: '#000000', fontWeight: 900, cursor: 'pointer',
            }}
          >
            PRINT / SAVE MY SCHEDULE
          </button>
        </section>
      </div>
    </main>
  );
};

export default PublicAppointmentSchedulePage;
